import * as Sentry from "@sentry/react";

declare const __APP_VERSION__: string | undefined;

/**
 * Check if an error is from Cloudflare Web Analytics beacon being blocked by
 * ad blockers / privacy extensions. These are harmless and should not be reported.
 */
function isCloudflareBeaconError(error: unknown): boolean {
  if (!error) return false;

  // Check error message
  const message = error instanceof Error ? error.message : String(error);
  if (!message) return false;

  // Cloudflare beacon blocked by client (resource load error)
  if (
    message.includes("ERR_BLOCKED_BY_CLIENT") &&
    message.includes("cloudflareinsights.com")
  ) {
    return true;
  }

  // Cloudflare beacon internal error when blocked (TypeError: Cannot read properties of undefined (reading 'startTime'))
  if (
    message.includes("Cannot read properties of undefined") &&
    message.includes("startTime") &&
    message.includes("cloudflareinsights.com")
  ) {
    return true;
  }

  // Check stack trace for cloudflareinsights.com
  const stack = error instanceof Error ? error.stack : undefined;
  if (stack && stack.includes("cloudflareinsights.com")) {
    return true;
  }

  return false;
}

Sentry.init({
  dsn: "https://bd805c7a6473532557f4f055c148dad5@o4509396845985792.ingest.us.sentry.io/4512188035104768",
  // Never report local dev (localhost HMR churn, e.g. stale
  // WelcomeBannerArt fast-refresh) to Sentry. Production only.
  enabled: import.meta.env.PROD,
  environment: import.meta.env.MODE,
  release:
    typeof __APP_VERSION__ !== "undefined"
      ? `attendeesync@${__APP_VERSION__}`
      : undefined,
  integrations: [Sentry.browserTracingIntegration()],
  // Docs default baseline: capture 100% of transactions for tracing.
  tracesSampleRate: 1.0,
  // Same-origin /api (Vite proxies to 127.0.0.1:8787) + local dev origins.
  tracePropagationTargets: [
    /^\//,
    /^http:\/\/localhost:5173/,
    /^http:\/\/127\.0\.0\.1:8787/,
  ],
  // Filter out Cloudflare beacon errors caused by ad blockers / privacy extensions
  beforeSend(event, hint) {
    // Belt-and-braces: drop anything from local dev even if enabled.
    if (!import.meta.env.PROD) return null;
    if (
      typeof window !== "undefined" &&
      (window.location.hostname === "localhost" ||
        window.location.hostname === "127.0.0.1")
    ) {
      return null;
    }
    const error = hint.originalException;
    if (isCloudflareBeaconError(error)) {
      return null; // drop the event
    }
    return event;
  },
});
